// src/api/taskTemplatesApi.ts

import { baseApi } from './baseApi';
import type {
  TaskTemplate,
  CreateTaskTemplateRequest,
  UpdateTaskTemplateRequest,
  ListTaskTemplatesParams,
} from '../types/api';

export const taskTemplatesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTaskTemplates: builder.query<TaskTemplate[], ListTaskTemplatesParams>({
      query: (params) => ({
        url: '/task-templates',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
          ...(params.isActive !== undefined && { isActive: params.isActive }),
        },
      }),
      providesTags: (result, error, params) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'TaskTemplate' as const, id })),
              { type: 'TaskTemplate', id: params.workspaceId || 'LIST' },
            ]
          : [{ type: 'TaskTemplate', id: params.workspaceId || 'LIST' }],
    }),

    getTaskTemplate: builder.query<TaskTemplate, string>({
      query: (id) => `/task-templates/${id}`,
      providesTags: (result, error, id) => [{ type: 'TaskTemplate', id }],
    }),

    createTaskTemplate: builder.mutation<TaskTemplate, CreateTaskTemplateRequest>({
      query: (body) => ({
        url: '/task-templates',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'TaskTemplate', id: workspaceId || 'LIST' },
      ],
    }),

    updateTaskTemplate: builder.mutation<TaskTemplate, { id: string } & UpdateTaskTemplateRequest>({
      query: ({ id, ...body }) => ({
        url: `/task-templates/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'TaskTemplate', id },
        { type: 'TaskTemplate', id: 'LIST' },
      ],
    }),

    deleteTaskTemplate: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/task-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'TaskTemplate', id },
        { type: 'TaskTemplate', id: 'LIST' },
      ],
    }),

    pauseTaskTemplate: builder.mutation<TaskTemplate, string>({
      query: (id) => ({
        url: `/task-templates/${id}/pause`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'TaskTemplate', id },
        { type: 'TaskTemplate', id: 'LIST' },
      ],
    }),

    resumeTaskTemplate: builder.mutation<TaskTemplate, string>({
      query: (id) => ({
        url: `/task-templates/${id}/resume`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'TaskTemplate', id },
        { type: 'TaskTemplate', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetTaskTemplatesQuery,
  useGetTaskTemplateQuery,
  useCreateTaskTemplateMutation,
  useUpdateTaskTemplateMutation,
  useDeleteTaskTemplateMutation,
  usePauseTaskTemplateMutation,
  useResumeTaskTemplateMutation,
} = taskTemplatesApi;
